import { useState } from 'react'
import { ChevronLeft, ChevronRight, Play, Code, Eye, FileCode } from 'lucide-react'

/**
 * Enhanced Session Viewer with p5.js Preview
 * Shows iteration-by-iteration code changes and live previews
 */
export default function SessionViewer({ session, onBack }) {
  const [currentIterationIndex, setCurrentIterationIndex] = useState(0)
  const [viewMode, setViewMode] = useState('split') // 'split', 'preview', 'code'
  const [showBefore, setShowBefore] = useState(false)
  
  if (!session || !session.iterations || session.iterations.length === 0) {
    return (
      <div className="h-screen bg-p5-dark text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">No iterations found for this session</p>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded"
          >
            ← Back
          </button>
        </div>
      </div>
    )
  }
  
  const currentIteration = session.iterations[currentIterationIndex]
  const totalIterations = session.iterations.length
  
  // Extract code from sketch JSON
  const getCodeFromSketch = (sketchJson) => {
    if (!sketchJson) return null
    try {
      const sketch = typeof sketchJson === 'string' ? JSON.parse(sketchJson) : sketchJson
      const jsFile = sketch.files?.find(f => f.name === 'sketch.js')
      return jsFile?.content || null
    } catch (e) {
      console.error('Failed to parse sketch:', e)
      return null
    }
  }
  
  const beforeCode = getCodeFromSketch(currentIteration.sketch_before_json)
  const afterCode = getCodeFromSketch(currentIteration.sketch_after_json)
  // Fall back to beforeCode if afterCode is null (common when user doesn't apply suggestion)
  const displayCode = showBefore ? beforeCode : (afterCode || beforeCode)
  
  const goToPrevious = () => {
    if (currentIterationIndex > 0) {
      setCurrentIterationIndex(currentIterationIndex - 1)
      setShowBefore(false)
    }
  }
  
  const goToNext = () => {
    if (currentIterationIndex < totalIterations - 1) {
      setCurrentIterationIndex(currentIterationIndex + 1)
      setShowBefore(false)
    }
  }
  
  return (
    <div className="h-screen bg-p5-dark text-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded transition-colors"
            >
              <ChevronLeft className="inline" size={16} /> Back
            </button>
            <div>
              <h2 className="text-xl font-bold">
                Session: {session.sessionId?.substring(0, 8)}...
              </h2>
              <p className="text-sm text-gray-400">
                {session.dyslexiaCategory === 'dyslexic' ? '🧠 Dyslexic' : 
                 session.dyslexiaCategory === 'non-dyslexic' ? '✅ Non-Dyslexic' : 
                 '❓ Status Unknown'} Participant • 
                {totalIterations} Iterations
              </p>
            </div>
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('split')}
              className={`px-3 py-2 rounded ${viewMode === 'split' ? 'bg-p5-pink' : 'bg-gray-800 hover:bg-gray-700'}`}
              title="Split View"
            >
              <FileCode size={18} />
            </button>
            <button
              onClick={() => setViewMode('preview')}
              className={`px-3 py-2 rounded ${viewMode === 'preview' ? 'bg-p5-pink' : 'bg-gray-800 hover:bg-gray-700'}`}
              title="Preview Only"
            >
              <Eye size={18} />
            </button>
            <button
              onClick={() => setViewMode('code')}
              className={`px-3 py-2 rounded ${viewMode === 'code' ? 'bg-p5-pink' : 'bg-gray-800 hover:bg-gray-700'}`}
              title="Code Only"
            >
              <Code size={18} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Navigation Bar */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <button
          onClick={goToPrevious}
          disabled={currentIterationIndex === 0}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={16} className="inline" /> Previous
        </button>
        
        <div className="text-center">
          <div className="text-lg font-semibold">
            Iteration {currentIterationIndex + 1} of {totalIterations}
          </div>
          <div className="text-xs text-gray-400">
            {currentIteration.prompt_pattern || 'unknown'} • 
            {currentIteration.provider} - {currentIteration.model}
          </div>
        </div>
        
        <button
          onClick={goToNext}
          disabled={currentIterationIndex === totalIterations - 1}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next <ChevronRight size={16} className="inline" />
        </button>
      </div>
      
      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden flex">
        {/* Left Panel: Conversation */}
        <div className="w-1/3 border-r border-gray-800 overflow-y-auto p-6 space-y-4">
          {/* User Prompt */}
          <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
            <div className="text-sm font-semibold text-p5-pink mb-2">Your Prompt:</div>
            <div className="text-sm text-gray-300 whitespace-pre-wrap">
              {currentIteration.prompt_text}
            </div>
            <div className="text-xs text-gray-500 mt-2">
              {currentIteration.prompt_length} chars • {currentIteration.prompt_word_count} words
              {currentIteration.has_code_snippet === 1 && ' • 📎 Has code'}
            </div>
          </div>
          
          {/* AI Response */}
          <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
            <div className="text-sm font-semibold text-p5-pink mb-2">AI Response:</div>
            <div className="text-sm text-gray-300 whitespace-pre-wrap max-h-96 overflow-y-auto">
              {currentIteration.response_text}
            </div>
          </div>
          
          {/* Metrics */}
          <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
            <div className="text-sm font-semibold text-p5-pink mb-2">Metrics:</div>
            <div className="space-y-1 text-sm text-gray-300">
              {currentIteration.rating && currentIteration.rating !== 'none' && (
                <div>Rating: {currentIteration.rating === 'up' ? '👍 Positive' : '👎 Negative'}</div>
              )}
              {currentIteration.applied_suggestion === 1 && (
                <div>✅ Applied suggestion</div>
              )}
              {currentIteration.lines_added > 0 && (
                <div>Code: <span className="text-green-400">+{currentIteration.lines_added}</span> / <span className="text-red-400">-{currentIteration.lines_removed || 0}</span></div>
              )}
            </div>
          </div>
        </div>
        
        {/* Right Panel: Code & Preview */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {(viewMode === 'split' || viewMode === 'code') && (
            <div className={`${viewMode === 'split' ? 'h-1/2' : 'h-full'} border-b border-gray-800 flex flex-col`}>
              <div className="bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-700 flex-shrink-0">
                <div className="text-sm font-semibold">Code</div>
                {afterCode && beforeCode && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowBefore(false)}
                      className={`px-3 py-1 rounded text-xs ${!showBefore ? 'bg-p5-pink' : 'bg-gray-700 hover:bg-gray-600'}`}
                    >
                      After
                    </button>
                    <button
                      onClick={() => setShowBefore(true)}
                      className={`px-3 py-1 rounded text-xs ${showBefore ? 'bg-p5-pink' : 'bg-gray-700 hover:bg-gray-600'}`}
                    >
                      Before
                    </button>
                  </div>
                )}
              </div>
              <div className="flex-1 overflow-auto bg-gray-900 p-4">
                <pre className="text-sm text-gray-300 font-mono">
                  <code>{displayCode || '// No code available'}</code>
                </pre>
              </div>
            </div>
          )}
          
          {(viewMode === 'split' || viewMode === 'preview') && (
            <div className={`${viewMode === 'split' ? 'h-1/2' : 'h-full'} flex flex-col`}>
              <div className="bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-700 flex-shrink-0">
                <div className="text-sm font-semibold">
                  <Play size={14} className="inline" /> p5.js Preview
                </div>
                <div className="text-xs text-gray-400">
                  {showBefore ? 'Before' : 'After'} state
                </div>
              </div>
              <div className="flex-1 bg-white overflow-hidden">
                {displayCode ? (
                  <P5Preview code={displayCode} key={`${currentIterationIndex}-${showBefore}`} />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    No code to preview
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * P5Preview Component - Runs p5.js code in an iframe
 */
function P5Preview({ code }) {
  const [error, setError] = useState(null)
  
  // Create HTML document with p5.js
  const createPreviewHTML = () => {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>p5.js Preview</title>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/p5.js/1.7.0/p5.min.js"></script>
  <style>
    body {
      margin: 0;
      padding: 0;
      overflow: hidden;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      background: #f0f0f0;
    }
    canvas {
      display: block;
    }
  </style>
</head>
<body>
  <script>
    // Error handling
    window.onerror = function(msg, url, lineNo, columnNo, error) {
      document.body.innerHTML = '<div style="padding: 20px; color: red; font-family: monospace;">Error: ' + msg + '</div>';
      return false;
    };
    
    // User code
    ${code}
  </script>
</body>
</html>
    `.trim()
  }
  
  return (
    <iframe
      srcDoc={createPreviewHTML()}
      className="w-full h-full border-0"
      sandbox="allow-scripts allow-same-origin"
      allow="camera; microphone; accelerometer; gyroscope; magnetometer; display-capture"
      title="p5.js Preview"
    />
  )
}
